import { Text } from '@react-pdf/renderer';
import React from 'react';
import { toast } from 'react-toastify';
import { getAPIKey } from '../services/login/login-service';
import { Common } from './constants';

type RenderMode = 'html' | 'pdf';

export const formatMessageWithBoldTitle = (message: string, renderAs: RenderMode = 'html'): React.ReactNode => {
  const regex = /##(.*?)##/;
  const match = message.match(regex);

  if (!match) {
    return renderAs === 'pdf' ? <Text>{message}</Text> : <div>{message}</div>;
  }
  const [fullMatch, boldText] = match;
  const parts = message.split(fullMatch);

  if (renderAs === 'pdf') {
    return (
      <Text>
        {parts[0]}
        <Text style={{ fontWeight: 'bold' }}>{boldText}</Text>
        {parts[1]}
      </Text>
    );
  } else {
    return (
      <>
        {parts[0]}
        <strong>{boldText}</strong>
        {parts[1]}
      </>
    );
  }
};

export const maskEmail = (email: string): string => {
  try {
    const [local, domainPart] = email.split('@');
    if (!domainPart) {
      return email;
    }

    const domainMatch = domainPart.match(/^(.)([^.]*)(\..+)$/);
    if (!domainMatch) {
      return email;
    }

    const [, firstChar, restBeforeDot, domainSuffix] = domainMatch;

    const maskedLocal = local.length <= 4 ? local : local.slice(0, 4) + '*'.repeat(local.length - 4);

    const maskedDomain = firstChar + '*'.repeat(restBeforeDot.length);

    return `${maskedLocal}@${maskedDomain}${domainSuffix}`;
  } catch {
    return email;
  }
};

export const maskPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';

  const formattedPhoneNumber = phoneNumber.replace(/\D/g, '');

  if (formattedPhoneNumber.length <= 4) return formattedPhoneNumber;

  const lastFour = formattedPhoneNumber.slice(-4);
  const masked = '*'.repeat(formattedPhoneNumber.length - 4);

  return masked + lastFour;
};

export const getISDCodeFromRegion = (selectedRegion?: string): string => {
  if (!selectedRegion) {
    return Common.defaultCountry.ISD_CODE;
  }

  return Common.REGION_TO_ISD_CODE[selectedRegion] ?? Common.defaultCountry.ISD_CODE;
};

export const getCountryCodeFromRegion = (selectedRegion?: string): string => {
  if (!selectedRegion) {
    return Common.defaultCountry.COUNTRY_CODE;
  }

  return Common.REGION_TO_COUNTRY_CODE[selectedRegion] ?? Common.defaultCountry.COUNTRY_CODE;
};

/**
 * Encrypts a plaintext password for backend validation.
 * @param {string} plainTextPassword - The user's input password.
 * @param {string} base64PublicKey - The raw Base64 public key from config.
 * @returns {Promise<string>} - The Base64 encoded encrypted password.
 */
export const encryptPassword = async (plainTextPassword: string, base64PublicKey: string) => {
  try {
    // 1. Decode the Base64 key string into an ArrayBuffer
    const binaryDerString = window.atob(base64PublicKey);
    const binaryDer = new Uint8Array(binaryDerString.length);
    for (let i = 0; i < binaryDerString.length; i++) {
      binaryDer[i] = binaryDerString.charCodeAt(i);
    }
    // 2. Import the Public Key
    const publicKey = await window.crypto.subtle.importKey(
      'spki',
      binaryDer.buffer,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      true,
      ['encrypt'],
    );
    // 3. Encrypt the password using OAEP with SHA-256
    const encodedPassword = new TextEncoder().encode(plainTextPassword);
    const encryptedBuffer = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, encodedPassword);

    // 4. Convert the encrypted buffer back to a Base64 string
    const encryptedArray = Array.from(new Uint8Array(encryptedBuffer));
    return window.btoa(String.fromCharCode.apply(null, encryptedArray));
  } catch (error) {
    console.error('Encryption failed:', error);
    throw error;
  }
};

/**
 * Encrypts an arbitrary-length plaintext (e.g. a callback URL) for backend validation.
 * Uses hybrid encryption: AES-256-GCM for the payload, RSA-OAEP to wrap the AES key.
 * @param {string} plainText - The value to encrypt.
 * @param {string} base64PublicKey - The raw Base64 public key from config.
 * @returns {Promise<string>} - Single Base64 string: [2-byte keyLen][wrappedKey][12-byte iv][ciphertext+tag]
 */
export const encryptURL = async (plainText: string, base64PublicKey: string): Promise<string> => {
  try {
    // 1. Decode the Base64 key string into an ArrayBuffer
    const binaryDerString = window.atob(base64PublicKey);
    const binaryDer = new Uint8Array(binaryDerString.length);
    for (let i = 0; i < binaryDerString.length; i++) {
      binaryDer[i] = binaryDerString.charCodeAt(i);
    }

    // 2. Import the RSA public key
    const publicKey = await window.crypto.subtle.importKey(
      'spki',
      binaryDer.buffer,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      true,
      ['encrypt'],
    );

    // 3. Generate a one-time AES-256-GCM key — this does the actual (unbounded-length) encryption
    const aesKey = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const cipherBuffer = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      new TextEncoder().encode(plainText),
    );

    // 4. RSA-wrap the small AES key
    const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);
    const wrappedKeyBuffer = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, rawAesKey);
    const wrappedKeyBytes = new Uint8Array(wrappedKeyBuffer);

    // 5. Pack into one buffer: [2-byte wrappedKey length][wrappedKey][iv][ciphertext+tag]
    const lenPrefix = new Uint8Array(2);
    new DataView(lenPrefix.buffer).setUint16(0, wrappedKeyBytes.length, false);

    const combined = new Uint8Array(lenPrefix.length + wrappedKeyBytes.length + iv.length + cipherBuffer.byteLength);
    combined.set(lenPrefix, 0);
    combined.set(wrappedKeyBytes, lenPrefix.length);
    combined.set(iv, lenPrefix.length + wrappedKeyBytes.length);
    combined.set(new Uint8Array(cipherBuffer), lenPrefix.length + wrappedKeyBytes.length + iv.length);

    // 6. Base64-encode the whole thing — single string
    let binary = '';
    combined.forEach((b) => (binary += String.fromCharCode(b)));
    return window.btoa(binary);
  } catch (error) {
    console.error('URL encryption failed:', error);
    throw error;
  }
};

export const handleErrorMessage = (error) => {
  toast.error(error.response?.data?.message || error.toString());
};

export const isSafeHttpUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    const parsedURL = new URL(url, window.location.origin);
    return parsedURL.protocol === 'http:' || parsedURL.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Fetches the RSA public key used to encrypt passwords on the client side
 * before sending them to the backend.
 *
 * @returns {Promise<string>} The RSA public key, or an empty string if the
 * key could not be retrieved.
 */
export const getRSAPasswordPublicKey = async () => {
  let rsaPasswordPublicKey = '';
  try {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.RSA_PASSWORD_PUBLIC_KEY,
    });
    rsaPasswordPublicKey =
      config.data.find((item) => item.reference_code === Common.ConfigurationKey.RSA_PASSWORD_PUBLIC_KEY)?.value ?? '';
  } catch {
    rsaPasswordPublicKey = '';
  }
  return rsaPasswordPublicKey;
};
